import {
  ChangeDetectionStrategy,
  booleanAttribute,
  Component,
  Directive,
  TemplateRef,
  computed,
  contentChild,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  MatBottomSheet,
  MatBottomSheetModule,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { FilterState } from './filter-state';
import { observeBandSize } from './band-size';

/** The band's filters, after the search field, in the fixed order (R1). */
@Directive({ selector: 'ng-template[appFilters]' })
export class AbookFiltersDirective {
  readonly template = inject(TemplateRef);
}

/** Filters behind "Flere filtre", folded out on a row of their own. */
@Directive({ selector: 'ng-template[appMoreFilters]' })
export class AbookMoreFiltersDirective {
  readonly template = inject(TemplateRef);
}

/**
 * The filter band on an admin list page (#855/#856). Search first (projected), then the filters
 * (`<ng-template appFilters>`), "Flere filtre" when the page has more, and "Nulstil filtre" once
 * anything differs from the page's standard (R4).
 *
 * The layout follows the band's own width (MD3 600/840/1200):
 * - **wide/medium** — everything in one row, wrapping in the same order.
 * - **narrow** — search on its own line; "Nulstil" becomes an icon button.
 * - **compact** — search, then "Filtre (n)" opening a bottom sheet with every filter, and the
 *   changed filters as chips in a horizontal row. An active filter is never hidden.
 *
 * ```html
 * <abook-filter-bar [state]="filters" [loading]="store.isPending()" [moreChanged]="moreChanged()"
 *                   [resultLabel]="'Vis ' + total() + ' bookinger'">
 *   <abook-filter-search … />
 *   <ng-template appFilters>
 *     <abook-filter-select icon="location_on" label="Anlæg" … />
 *     <abook-period-chip … />
 *   </ng-template>
 *   <ng-template appMoreFilters>…</ng-template>
 * </abook-filter-bar>
 * ```
 */
@Component({
  selector: 'abook-filter-bar',
  imports: [
    NgTemplateOutlet,
    MatBottomSheetModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'abook-filter-bar',
    '[attr.data-size]': 'size()',
  },
  templateUrl: './abook-filter-bar.component.html',
  styleUrl: './abook-filter-bar.component.scss',
})
export class AbookFilterBarComponent {
  private readonly bottomSheet = inject(MatBottomSheet);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- any page's filters
  readonly state = input.required<FilterState<any>>();
  /** A fetch is running: a thin line under the band (R14). */
  readonly loading = input(false, { transform: booleanAttribute });
  /** 36px chips and a 26px band radius. */
  readonly compact = input(false, { transform: booleanAttribute });
  /** How many of the filters behind "Flere filtre" are changed. They are then always shown. */
  readonly moreChanged = input(0);
  /** The bottom sheet's close button: "Vis 64 bookinger". */
  readonly resultLabel = input('Vis resultater');

  protected readonly filters = contentChild(AbookFiltersDirective);
  protected readonly moreFilters = contentChild(AbookMoreFiltersDirective);
  private readonly sheetTemplate = viewChild.required<TemplateRef<unknown>>('sheet');

  protected readonly size = observeBandSize();
  protected readonly isCompact = computed(() => this.size() === 'compact');
  protected readonly isNarrow = computed(
    () => this.size() === 'narrow' || this.size() === 'compact',
  );

  private readonly moreOpen = signal(false);
  /** Folded out by hand, or because something in it is changed — an active filter is never hidden. */
  protected readonly moreExpanded = computed(() => this.moreOpen() || this.moreChanged() > 0);

  private sheetRef: MatBottomSheetRef | null = null;

  /** Folds in only when nothing in it is changed. */
  protected toggleMore(): void {
    this.moreOpen.set(!this.moreOpen());
  }

  protected openSheet(): void {
    this.sheetRef = this.bottomSheet.open(this.sheetTemplate(), {
      panelClass: 'abook-filter-sheet',
      ariaLabel: 'Filtre',
    });
    this.sheetRef.afterDismissed().subscribe(() => (this.sheetRef = null));
  }

  protected closeSheet(): void {
    this.sheetRef?.dismiss();
  }
}
